import React, { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { examAPI, examInfoAPI } from '../lib/api';
import { getExamInfo, getExamInfoByName } from '../config/examInfo';
import { 
  BookOpen, Calendar, FileText, Users, Globe, Target, Clock, Award,
  ArrowRight, Check, AlertCircle, ExternalLink, GraduationCap, Briefcase,
  Building2, DollarSign, TrendingUp, Info, ChevronRight, Star, MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import LoadingSpinner from '../components/LoadingSpinner';

const ExamInfo = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [detailedInfo, setDetailedInfo] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExamInfo();
  }, [examId]);

  const loadExamInfo = async () => {
    try {
      const examRes = await examAPI.getById(examId);
      setExam(examRes.data);
      
      // Try to get detailed info from API (admin-managed) first
      let info = null;
      try {
        const infoRes = await examInfoAPI.getByExamId(examId);
        if (infoRes.data?.info_data) {
          info = infoRes.data.info_data;
        }
      } catch (apiErr) {
        // API info not available - fall through to static config
      }
      
      // Fallback to static config
      if (!info) {
        info = getExamInfoByName(examRes.data?.name);
        if (!info) {
          info = getExamInfo(examId);
        }
      }
      setDetailedInfo(info);
    } catch (error) {
      toast.error('Failed to load exam information');
      logger.error(error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Info },
    { id: 'eligibility', label: 'Eligibility', icon: Users },
    { id: 'careers', label: 'Career Scope', icon: Briefcase },
    { id: 'colleges', label: 'Top Colleges', icon: GraduationCap },
    { id: 'preparation', label: 'Preparation Tips', icon: BookOpen },
  ];

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner size="lg" text="Loading exam information..." />
      </Layout>
    );
  }

  if (!exam) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto py-12 text-center">
          <AlertCircle className="mx-auto mb-4 text-muted-foreground" size={48} />
          <h1 className="text-2xl font-bold text-foreground mb-2">Exam Not Found</h1>
          <Button onClick={() => navigate('/exams')} className="mt-4">
            Back to Exams
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-6 mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <h1 className="text-3xl font-bold text-foreground">{exam.name}</h1>
                <Badge variant="secondary">{exam.category?.toUpperCase()}</Badge>
              </div>
              {detailedInfo && (
                <p className="text-lg text-muted-foreground mb-2">{detailedInfo.fullName}</p>
              )}
              <p className="text-muted-foreground">{exam.description}</p>
              
              {detailedInfo && (
                <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Building2 size={14} />
                    {detailedInfo.conductedBy}
                  </span>
                  <a 
                    href={detailedInfo.officialWebsite} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-primary hover:underline"
                  >
                    <ExternalLink size={14} />
                    Official Website
                  </a>
                </div>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button onClick={() => navigate(`/test/create?exam=${examId}`)}>
                <Target className="mr-2" size={16} />
                Create Test
              </Button>
              <Button onClick={() => navigate(`/practice/${examId}`)} variant="outline">
                <BookOpen className="mr-2" size={16} />
                Practice
              </Button>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {exam.config && (
            <>
              <Card className="p-4 border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <FileText className="text-blue-600 dark:text-blue-400" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{exam.config.total_questions}</p>
                    <p className="text-sm text-muted-foreground">Questions</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <Clock className="text-green-600 dark:text-green-400" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{exam.config.duration_minutes}m</p>
                    <p className="text-sm text-muted-foreground">Duration</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <Award className="text-purple-600 dark:text-purple-400" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{exam.config.total_marks}</p>
                    <p className="text-sm text-muted-foreground">Total Marks</p>
                  </div>
                </div>
              </Card>
              <Card className="p-4 border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                    <BookOpen className="text-orange-600 dark:text-orange-400" size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{exam.subjects?.length || exam.config.sections?.length || 3}</p>
                    <p className="text-sm text-muted-foreground">Subjects</p>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>

        {/* Navigation Tabs */}
        {detailedInfo && (
          <div className="border-b border-border mb-6">
            <div className="flex gap-1 overflow-x-auto">
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-3 text-sm font-medium whitespace-nowrap flex items-center gap-2 border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <IconComponent size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Tab Content */}
        {detailedInfo && (
          <div className="mb-8">
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* About */}
                <Card className="p-6 border border-border">
                  <h2 className="text-xl font-semibold mb-4">About {exam.name}</h2>
                  <p className="text-muted-foreground leading-relaxed">{detailedInfo.about}</p>
                </Card>

                {/* Exam Pattern */}
                {detailedInfo.examPattern && (
                  <Card className="p-6 border border-border">
                    <h2 className="text-xl font-semibold mb-4">Exam Pattern</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Mode</span>
                          <span className="font-medium">{detailedInfo.examMode}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Duration</span>
                          <span className="font-medium">{detailedInfo.examPattern.duration}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Total Questions</span>
                          <span className="font-medium">{detailedInfo.examPattern.totalQuestions}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">Total Marks</span>
                          <span className="font-medium">{detailedInfo.examPattern.totalMarks}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">Sections</p>
                        <div className="space-y-2">
                          {detailedInfo.examPattern.sections?.map((section, idx) => (
                            <div key={idx} className="p-3 bg-secondary/50 rounded-lg text-sm">
                              {section}
                            </div>
                          ))}
                        </div>
                        {detailedInfo.examPattern.marking && (
                          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-sm">
                            <p className="font-medium text-yellow-800 dark:text-yellow-200">Marking Scheme</p>
                            <p className="text-yellow-700 dark:text-yellow-300">{detailedInfo.examPattern.marking}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                )}

                {/* Important Dates */}
                {detailedInfo.importantDates && (
                  <Card className="p-6 border border-border">
                    <h2 className="text-xl font-semibold mb-4">Important Dates (2025-2026)</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {Object.entries(detailedInfo.importantDates).map(([session, dates]) => (
                        <div key={session} className="p-4 bg-secondary/30 rounded-lg">
                          <h3 className="font-medium mb-3 capitalize">{session.replace(/([A-Z])/g, ' $1').trim()}</h3>
                          <div className="space-y-2 text-sm">
                            {Object.entries(dates).map(([event, date]) => (
                              <div key={event} className="flex justify-between">
                                <span className="text-muted-foreground capitalize">{event}</span>
                                <span className="font-medium">{date}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Why This Exam */}
                {detailedInfo.whyThisExam && (
                  <Card className="p-6 border border-border">
                    <h2 className="text-xl font-semibold mb-4">Why {exam.name}?</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {detailedInfo.whyThisExam.map((reason, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                          <Check className="text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" size={16} />
                          <span className="text-sm">{reason}</span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            )}

            {/* Eligibility Tab */}
            {activeTab === 'eligibility' && detailedInfo.eligibility && (
              <Card className="p-6 border border-border">
                <h2 className="text-xl font-semibold mb-6">Eligibility Criteria</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {Object.entries(detailedInfo.eligibility).map(([key, value]) => (
                    <div key={key} className="p-4 bg-secondary/30 rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </p>
                      <p className="font-medium">{value}</p>
                    </div>
                  ))}
                </div>
                
                {detailedInfo.applicationFee && (
                  <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h3 className="font-medium mb-3">Application Fee</h3>
                    <div className="flex flex-wrap gap-4">
                      {Object.entries(detailedInfo.applicationFee).map(([category, fee]) => (
                        <div key={category} className="text-sm">
                          <span className="text-muted-foreground capitalize">{category}: </span>
                          <span className="font-medium">{fee}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Careers Tab */}
            {activeTab === 'careers' && detailedInfo.careerOpportunities && (
              <div className="space-y-4">
                <Card className="p-6 border border-border">
                  <h2 className="text-xl font-semibold mb-2">Career Opportunities</h2>
                  <p className="text-muted-foreground mb-6">
                    Explore the diverse career paths available after qualifying {exam.name}
                  </p>
                  
                  <div className="space-y-4">
                    {detailedInfo.careerOpportunities.map((career, idx) => (
                      <div key={idx} className="p-5 border border-border rounded-lg hover:border-primary/50 transition-colors">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-semibold text-lg">{career.title}</h3>
                            <p className="text-sm text-muted-foreground">{career.description}</p>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                              <DollarSign size={16} />
                              <span className="font-semibold">{career.avgSalary}</span>
                            </div>
                            <p className="text-xs text-muted-foreground">Avg. Package</p>
                          </div>
                        </div>
                        
                        <div>
                          <p className="text-xs text-muted-foreground mb-2">Top Recruiters</p>
                          <div className="flex flex-wrap gap-2">
                            {career.topRecruiters.map((recruiter, ridx) => (
                              <Badge key={ridx} variant="secondary" className="text-xs">
                                {recruiter}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            )}

            {/* Colleges Tab */}
            {activeTab === 'colleges' && detailedInfo.topColleges && (
              <Card className="p-6 border border-border">
                <h2 className="text-xl font-semibold mb-6">Top Colleges</h2>
                <div className="space-y-3">
                  {detailedInfo.topColleges.map((college, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-4 bg-secondary/30 rounded-lg"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                          {idx + 1}
                        </div>
                        <div>
                          <h3 className="font-medium">{college.name}</h3>
                          {college.cutoff && (
                            <p className="text-sm text-muted-foreground">Cutoff: {college.cutoff}</p>
                          )}
                        </div>
                      </div>
                      {college.ranking && (
                        <div className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
                          <Star size={14} />
                          <span className="text-sm font-medium">Rank #{college.ranking}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Preparation Tab */}
            {activeTab === 'preparation' && detailedInfo.preparationTips && (
              <Card className="p-6 border border-border">
                <h2 className="text-xl font-semibold mb-6">Preparation Tips</h2>
                <div className="space-y-3">
                  {detailedInfo.preparationTips.map((tip, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-start gap-4 p-4 bg-secondary/30 rounded-lg"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-sm font-bold text-primary-foreground flex-shrink-0">
                        {idx + 1}
                      </div>
                      <p className="text-sm pt-1.5">{tip}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}

        {/* Fallback for exams without detailed info */}
        {!detailedInfo && (
          <div className="space-y-6">
            {/* Subjects */}
            {exam.subjects && exam.subjects.length > 0 && (
              <Card className="p-6 border border-border">
                <h2 className="text-xl font-semibold mb-4">Subjects</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {exam.subjects.map((subject, idx) => (
                    <div key={idx} className="p-3 rounded-lg bg-secondary/50 text-center">
                      <p className="font-medium">{subject}</p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {/* Exam Pattern from config */}
            {exam.config?.sections && exam.config.sections.length > 0 && (
              <Card className="p-6 border border-border">
                <h2 className="text-xl font-semibold mb-4">Exam Pattern</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-border">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold">Section</th>
                        <th className="text-left py-3 px-4 font-semibold">Questions</th>
                        <th className="text-left py-3 px-4 font-semibold">Marks</th>
                        <th className="text-left py-3 px-4 font-semibold">Time (min)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exam.config.sections.map((section, idx) => (
                        <tr key={idx} className="border-b border-border/50">
                          <td className="py-3 px-4 font-medium">{section.name}</td>
                          <td className="py-3 px-4 text-muted-foreground">{section.questions}</td>
                          <td className="py-3 px-4 text-muted-foreground">{section.marks}</td>
                          <td className="py-3 px-4 text-muted-foreground">{section.duration_minutes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        )}

        {/* CTA Section */}
        <Card className="p-8 bg-primary text-primary-foreground border-0 text-center">
          <h2 className="text-2xl font-bold mb-3">Ready to Start Preparing?</h2>
          <p className="text-primary-foreground/80 mb-6 max-w-xl mx-auto">
            Practice with real exam patterns, get instant feedback, and track your progress.
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button 
              onClick={() => navigate(`/test/create?exam=${examId}`)}
              variant="secondary"
            >
              <Target className="mr-2" size={16} />
              Start Test
            </Button>
            <Button 
              onClick={() => navigate(`/practice/${examId}`)}
              variant="outline"
              className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
            >
              <BookOpen className="mr-2" size={16} />
              Practice Questions
            </Button>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default ExamInfo;
